import { ListSubheader, MenuItem, Select, Typography } from "@mui/material";
import { useListBillingProducts } from "services/billing";
import { useEffect } from "react";
import { BillingProduct } from "protogen/billing_pb";
import { ChevronDown } from "lucide-react";

type Props = {
  value?: BillingProduct;
  onUpdate: (p?: BillingProduct) => void;
  disabled: boolean;
  stacked: boolean;
  isAdmin: boolean;
  widthPercentage: number;
  initialProductName?: string;
  fullWidth?: boolean;
};

export default ({
  value,
  onUpdate,
  stacked,
  disabled,
  isAdmin,
  widthPercentage,
  initialProductName,
  fullWidth = false,
}: Props) => {
  const { request, data, loading } = useListBillingProducts();
  const fetchProducts = async () => {
    const resp = await request();
    if (resp && initialProductName) {
      const match = resp.products.find((p) => p.name === initialProductName);
      if (match) {
        onUpdate(match);
      }
    }
  };
  useEffect(() => {
    if (!disabled && !data) {
      fetchProducts();
    }
  }, [disabled]);
  const options = (data?.products || (value ? [value] : [])).sort(
    (a, b) => a.amountCents - b.amountCents,
  );
  const mainOfferings = new Set([
    // Prod.
    "prod_QGpeOl3cDuxaiZ", // Total Game Changer
    "prod_Qo2mOQl22DIJIt", // Game changer (lite)
    "prod_QKBrlo0j4DuINP", // Free Time Finder
    "prod_QTbbvlRkHU1GKk", // Peace of Mind
    "prod_SOGyI3OwX4eanA", // Summer Pause
    // Dev.
    "prod_QZAxZCP1JFyGGW",
    "prod_QZAx7GkKumuhHy",
    "prod_QZAvacnqgiPvls",
  ]);
  const toProduct = (product: BillingProduct) => (
    <MenuItem
      key={product.id}
      value={product.id}
      disabled={!product.amountCents}
    >
      <div>
        <Typography
          variant="subtitle1"
          sx={{
            textWrap: "nowrap",
            textOverflow: "ellipsis",
            overflowX: "hidden",
          }}
        >
          <span style={{ fontWeight: "600", marginRight: "6px" }}>
            ${`${(product.amountCents / 100).toFixed(2)}`}/mo
          </span>
          {product.name}
        </Typography>
        <Typography
          variant="body2"
          color="textSecondary"
          sx={{
            textWrap: "nowrap",
            textOverflow: "ellipsis",
            overflowX: "hidden",
          }}
        >
          {product.description}
        </Typography>
      </div>
    </MenuItem>
  );
  const showAllProducts =
    isAdmin && options.filter((p) => !mainOfferings.has(p.id)).length > 0;
  return (
    <Select
      label="Service tier"
      margin="none"
      displayEmpty
      value={value?.id || ""}
      disabled={loading || disabled}
      onChange={(e) => {
        const val = e.target.value;
        onUpdate(options.find((p) => p.id === val) || undefined);
      }}
      sx={{
        "& .MuiInputBase-input": {
          marginTop: "0px !important",
        },
        width:
          stacked || fullWidth ? "100%" : `calc(${widthPercentage}% - 8px)`,
        height: "60px",
      }}
      IconComponent={ChevronDown}
    >
      <MenuItem key={""} value={""} disabled>
        Service tier
      </MenuItem>
      {options.filter((p) => mainOfferings.has(p.id)).map(toProduct)}
      {showAllProducts && <ListSubheader>Additional Offerings</ListSubheader>}
      {showAllProducts &&
        options.filter((p) => !mainOfferings.has(p.id)).map(toProduct)}
    </Select>
  );
};
